import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Route, Routes, useLocation, useMatch } from 'react-router-dom'

import { Navigate } from '@lib/react-router-dom'

import { Referral } from '@pages/Referral/Referral'

import { WebSocketBridge, WebSocketProvider } from '@features/websocket'

import { useAccountReadContext } from '@context/AccountContextContext'
import { AccountInfoLimitsContextProvider } from '@context/AccountInfoContext/AccountInfoContext.Provider'
import { AuthSessionContext, AuthSessionProvider } from '@context/AuthContext'
import { CustomerSupportProvider } from '@context/CustomerSupportContext'
import { EntitySettingsContextProvider } from '@context/EntityContext'
import { useFeatureFlags } from '@context/FeatureFlagsContext'
import { LegalLinksProvider } from '@context/LegalLinksContext'
import { LossPercentagesContextProvider } from '@context/LossPercentagesContext'
import { useMaintenance } from '@context/MaintenanceModeContext'
import { ModalsContextProvider } from '@context/ModalsContext'
import { PathHistoryProvider } from '@context/PathHistoryContext'
import { ProductContextProvider, useProductWriteContext } from '@context/ProductContext'
import { PublicDomainsContextProvider } from '@context/PublicDomainsContext'
import { SessionSettingsContextProvider } from '@context/SessionSettingsContext'
import { SessionTimeoutProvider } from '@context/SessionTimeoutContext'
import { SignupContextProvider } from '@context/SignupContext'
import { SnackbarContextProvider, useSnackbar } from '@context/SnackbarContext'
import { SupportContactsProvider } from '@context/SupportContactsContext'
import { TradingAccountEntityConfigurationProvider } from '@context/TradingAccountEntityConfigurationContext'
import { WalletsContextProvider } from '@context/UserWalletContext'

import { ApiClientContext, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { entityPerTickmillCompany, getRedirectDomain } from '@utils/companyName.utils'
import { isLoginPage as checkIfLoginPage } from '@utils/isLoginPage'
import { CommonPaths, isSignUpPath } from '@utils/path'

import { AuthUser } from '@model/User'

import { LocaleModule } from '@global/LocaleModule'
import { AllLocales, Locale } from '@global/locale/Locale'
import { detectThemeClientSide } from '@global/theme/Theme'

import { useGoogleAnalytics } from '@hooks/useGoogleAnalytics'
import { useLogout } from '@hooks/useLogout'
import { useScrollbarWidth } from '@hooks/useScrollbarWidth'
import { useLocallyPersistedState } from '@hooks/useStorage'

import { PageNoticeContextProvider } from '@shared/components/PageNotice/PageNoticeContext'

import { useAmplitude } from '@analytics/amplitude'
import { useClarity } from '@analytics/clarity'
import { useConsentWithdrawalCleanup } from '@analytics/consent'

import i18n from '../../i18n'
import { MainContainer } from '../MainContainer'
import { useErrorHandler } from './ErrorHandler'

interface RoutingProps {
  urlLocale: Locale
  setCurrentPath: (path: string) => void
}

const Routing = (props: RoutingProps) => {
  const { urlLocale, setCurrentPath } = props
  const { account } = useAccountReadContext()
  const [auth] = useContext(AuthSessionContext)
  const apiClient = useContext(ApiClientContext)
  const clientApiClient = useApiClient(ClientApiClient)
  const { setMaintenance } = useMaintenance()

  const { changeProductContext } = useProductWriteContext()
  const { errorHandler } = useErrorHandler()
  const location = useLocation()
  const { logout } = useLogout()
  const isLoginPage = checkIfLoginPage(location.pathname)
  const [productHandled, setProductHandled] = useLocallyPersistedState<boolean>(
    'productHandled',
    false
  )
  const removeSession = (a: AuthUser) => {
    logout()
    window.location.replace('https://' + getRedirectDomain(a.tickmillCompany.id))
  }

  useEffect(() => {
    setTimeout(() => {
      if (!auth) {
        return
      }
      if (isLoginPage) {
        return
      }
      if (window.env?.APP_ENTITY?.includes(entityPerTickmillCompany[auth.tickmillCompany.id])) {
        return
      }
      if (process.env.NODE_ENV === 'development') {
        return
      }

      console.debug('Wrong Entity. Logout!')
      clientApiClient.logOut(auth.id).finally(() => removeSession(auth))
    }, 3000)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, isLoginPage])

  useEffect(() => {
    if (!apiClient) {
      return
    }
    apiClient.updateMaintenanceMode = () => setMaintenance()

    apiClient.errorHandler = errorHandler
  }, [apiClient, errorHandler, setMaintenance])

  useEffect(() => {
    if (!auth || !account) {
      setProductHandled(false)
    }
  }, [auth, account, setProductHandled])

  useEffect(() => {
    const productId = account?.preferredLandingPage?.tickmillProductId
    if (!productHandled && productId) {
      changeProductContext(productId)
      setProductHandled(true)
    }
    return
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, location.pathname])

  const isCompromisedPage = useMatch(`/:locale${CommonPaths.PasswordCompromised}`)
  const isLogoutPage = useMatch('/:locale/logout')
  const shouldRedirectToCompromised =
    auth && auth?.isCompromised && !isCompromisedPage && !isLogoutPage

  if (shouldRedirectToCompromised) {
    return <Navigate to={CommonPaths.PasswordCompromised} replace />
  }

  return (
    <Routes>
      {AllLocales.map((locale) => (
        <Route
          key={locale}
          path={`/${locale}/*`}
          element={<MainContainer urlLocale={urlLocale} setCurrentPath={setCurrentPath} />}
        />
      ))}
    </Routes>
  )
}

const QueryErrorSnackbarBridge: React.FC = () => {
  const { addSnackbar } = useSnackbar()

  useEffect(() => {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<{ source?: 'query' | 'mutation'; error?: unknown }>
      const source = customEvent.detail?.source ?? 'query'
      const suffix = source === 'mutation' ? 'Request failed' : 'Data refresh failed'
      addSnackbar.error({ message: suffix })
    }

    window.addEventListener('app:query-error', handler as EventListener)
    return () => window.removeEventListener('app:query-error', handler as EventListener)
  }, [addSnackbar])

  return null
}

export const Session: React.FC = () => {
  useScrollbarWidth()
  const location = useLocation()
  const [applyTheme, setApplyTheme] = useState(false)
  const [currentPath, setCurrentPath] = useState(location.pathname)
  const { darkMode } = useFeatureFlags()
  const [pathHistory, setPathHistory] = useState<string[]>([])

  useGoogleAnalytics()
  useAmplitude()
  useClarity()
  useConsentWithdrawalCleanup()

  const rootDiv = document.getElementById('root')
  if (rootDiv) {
    rootDiv.setAttribute('dir', i18n.dir())
  }

  const locale = (i18n.language === 'cimode' ? 'en' : i18n.language || 'en') as Locale

  useEffect(() => {
    setApplyTheme(!isSignUpPath(currentPath))
  }, [currentPath])

  const getUrlLocale = useMemo(() => {
    const urlLocale = window.location.pathname.substring(1, 3) as Locale
    if (AllLocales.includes(urlLocale)) {
      return urlLocale as Locale
    } else {
      return 'en' as Locale
    }
  }, [])

  const trackLastPathsVisited = () => {
    if (pathHistory.length === 0 || pathHistory[pathHistory.length - 1] !== location.pathname) {
      const newPathHistory = [...pathHistory, location.pathname].slice(-2)
      setPathHistory(newPathHistory)
    }
  }

  useEffect(() => {
    trackLastPathsVisited()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  return (
    <SessionSettingsContextProvider
      initialSettings={{
        locale: locale as Locale,
        theme: darkMode ? detectThemeClientSide() : 'light',
      }}
      applyTheme={applyTheme}
    >
      <PathHistoryProvider currentPathHistory={pathHistory}>
        <Referral>
          <AuthSessionProvider>
            <EntitySettingsContextProvider>
              <SupportContactsProvider>
                <PublicDomainsContextProvider>
                  <ProductContextProvider>
                    <TradingAccountEntityConfigurationProvider>
                      <LossPercentagesContextProvider>
                        <LegalLinksProvider>
                          <ModalsContextProvider>
                            <AccountInfoLimitsContextProvider>
                              <SnackbarContextProvider>
                                <WebSocketProvider>
                                  <WebSocketBridge />
                                  <QueryErrorSnackbarBridge />
                                  <SignupContextProvider>
                                    <SessionTimeoutProvider>
                                      <PageNoticeContextProvider>
                                        <WalletsContextProvider>
                                          <CustomerSupportProvider>
                                            <LocaleModule />
                                            <Routing
                                              setCurrentPath={setCurrentPath}
                                              urlLocale={getUrlLocale}
                                            />
                                          </CustomerSupportProvider>
                                        </WalletsContextProvider>
                                      </PageNoticeContextProvider>
                                    </SessionTimeoutProvider>
                                  </SignupContextProvider>
                                </WebSocketProvider>
                              </SnackbarContextProvider>
                            </AccountInfoLimitsContextProvider>
                          </ModalsContextProvider>
                        </LegalLinksProvider>
                      </LossPercentagesContextProvider>
                    </TradingAccountEntityConfigurationProvider>
                  </ProductContextProvider>
                </PublicDomainsContextProvider>
              </SupportContactsProvider>
            </EntitySettingsContextProvider>
          </AuthSessionProvider>
        </Referral>
      </PathHistoryProvider>
    </SessionSettingsContextProvider>
  )
}
